<script setup>
import { ref, computed, watch } from 'vue'
import FieldCreator from '../UI/Form/FieldCreator.vue'
import UserSelect from '../UI/Form/UserSelect.vue'
import EntityLink from '../UI/Button/EntityLink.vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
  contract: { type: Object, required: true },
  def: { type: Object, required: true },
  formData: { type: Object, required: true },
  isV2: { type: Boolean, required: true },
})

const emit = defineEmits(['add-salesman', 'change-supplier', 'add-director'])

const directorId = ref(null)

const createdBy = computed(() => {
  if (props.contract.analyst) {
    return props.contract.analyst.name
  }
  return ''
})

const supplierId = computed(() => props.contract?.supplier?.id)

const sirenClientLabel = computed(() => {
  if (props.contract?.client?.countryCode === 'ES') {
    return t('entity.nif.client')
  }
  return t('simulation.fields.sirenClient.label')
})

const sirenSupplierLabel = computed(() => {
  if (props.contract?.supplier?.countryCode === 'ES') {
    return t('entity.nif.supplier')
  }
  return t('simulation.fields.sirenSupplier.label')
})

const changeSupplier = (siren, choices, label, fieldName, entity) => {
  emit('change-supplier', siren, choices, label, fieldName, entity)
}

const directorChange = (id) => {
  directorId.value = id
}

watch(
  props.contract,
  (newContract) => {
    directorId.value = newContract.director ? newContract.director.id : null
  },
  { deep: true, immediate: true },
)
</script>

<template>
  <div>
    <div class="row mb-3">
      <div class="col">
        {{ $t('contract.information.client') }}
      </div>
      <div class="col">
        <EntityLink v-if="contract.client" :entity="contract.client" e2e-data="contractClientName" />
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        {{ sirenClientLabel }}
      </div>
      <div class="col" e2e-data="contractClientSiren">
        {{ contract.client ? contract.client.siren : '' }}
      </div>
    </div>
    <FieldCreator :form-data="formData" name="owner" form-ref="formcontract" domain="contract" label-left-equal />
    <FieldCreator
      :form-data="formData"
      label-left-equal
      name="director"
      form-ref="formcontract"
      domain="contract"
      add-director
      :director-id="contract?.director?.id"
      :finance-id="contract.finance.id"
      :entity-id="contract.client.id"
      @get-select-change="directorChange"
    />
    <UserSelect
      v-if="!isV2"
      :form-data="formData"
      label-wrapper-classes="col-md-6 d-flex justify-content-between"
      input-wrapper-classes="col-md-6"
      name="buyer"
      form-ref="formcontract"
      domain="contract"
      add-user
      :contract-id="contract?.id"
      :client-id="contract?.client?.id"
    />
    <hr style="border-top: 1px solid rgba(25, 55, 105, 0.15)" />
    <div class="row mb-3">
      <div class="col">
        {{ $t('main.supplier') }}
      </div>
      <div class="col">
        <EntityLink v-if="contract.supplier" :entity="contract.supplier" e2e-data="contractSupplierName" />
        <FieldCreator
          v-else
          :form-data="formData"
          name="sirenSupplier"
          form-ref="formcontract"
          domain="contract"
          type-value="string"
          value-property="id"
          not-remove
          add-entity
          @get-select-change="changeSupplier"
          @entity-submitted="changeSupplier"
        />
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        {{ sirenSupplierLabel }}
      </div>
      <div class="col" e2e-data="contractSupplierSiren">
        {{ contract.supplier ? contract.supplier.siren : '' }}
      </div>
    </div>
    <div v-if="supplierId" class="mb-3">
      <UserSelect
        label-wrapper-classes="col-md-6 d-flex justify-content-between"
        input-wrapper-classes="col-md-6"
        name="salesman"
        form-ref="formcontract"
        domain="contract"
        add-user
        :form-data="formData"
        :contract-id="contract?.id"
        :client-id="supplierId"
      />
    </div>
    <hr style="border-top: 1px solid rgba(25, 55, 105, 0.15)" />
    <div class="row mb-3">
      <div class="col">
        {{ $t('contract.fields.companySalesman.label') }}
      </div>
      <div class="col" e2e-data="contractCompanySalesmanName">
        {{ contract.companySalesman ? contract.companySalesman.name : '' }}
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        {{ $t('contract.fields.platformSalesman.label') }}
      </div>
      <div class="col" e2e-data="contractPlatformSalesmanName">
        {{ contract.platformSalesman ? contract.platformSalesman.name : '' }}
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        {{ $t('demand.fields.createdBy.label') }}
      </div>
      <div class="col" e2e-data="contractCreatedBy">
        {{ createdBy }}
      </div>
    </div>
    <div class="row mb-3">
      <div class="col">
        {{ $t('contract.fields.managerSalesman.label') }}
      </div>
      <div class="col" e2e-data="contractManagerSalesmanName">
        {{ contract.managerSalesman ? contract.managerSalesman.name : '' }}
      </div>
    </div>
  </div>
</template>
